import type { PositionRisk } from '@/features/home/lib/home-risk'
import { Denomination, type Position } from '@/types/generated/common/trading'
import type { Exchange } from '@/types/generated/common/types'

/**
 * What the user is exposed to, per underlying asset: the open positions (and their risk read)
 * and open orders grouped by the coin they're on, sized in USD. Home's daily brief picks its
 * datapoints and its trade ideas from this. Pure (tested in __tests__/home-exposure.test.ts).
 */

const QUOTE_SUFFIX = /(USDT|USDC|USD|PERP|PERPETUAL)$/

/**
 * The coin a symbol is on, across venue formats: "BTC-PERPETUAL", "ETH_USDC-PERPETUAL",
 * "ETHUSDT-PERP", "SOL-28MAR25-150-C", "kPEPEUSDT", "BTC" → BTC, ETH, ETH, SOL, KPEPE, BTC.
 */
export function baseAssetOf(symbol: string): string {
  const head = symbol.toUpperCase().split(/[-_/:]/)[0] ?? ''
  let base = head
  // "ETHUSDT" style: peel known quote/contract suffixes off the first segment.
  for (let i = 0; i < 2 && QUOTE_SUFFIX.test(base) && base.length > 4; i++) {
    base = base.replace(QUOTE_SUFFIX, '')
  }
  return base || head
}

/** A position's size in USD, unsigned: venue delta when reported, else size × mark. */
export function positionNotionalUsd(position: Position): number {
  const delta = Number(position.deltaUsd)
  if (position.deltaUsd && Number.isFinite(delta) && delta !== 0) return Math.abs(delta)
  const quantity = Math.abs(Number(position.quantity) || 0)
  if (position.sizeDenomination === Denomination.DENOMINATION_QUOTE) return quantity
  const mark = Number(position.markPrice) || Number(position.avgPrice) || 0
  return quantity * mark
}

const OPTION_SYMBOL = /-\d{1,2}[A-Z]{3}\d{2}-\d+(\.\d+)?-[CP]$/i

export function isOptionSymbol(symbol: string): boolean {
  return OPTION_SYMBOL.test(symbol)
}

export function isPerpSymbol(symbol: string): boolean {
  return /PERP/i.test(symbol)
}

export interface AssetExposure {
  asset: string
  /** Sum of the positions' USD sizes. */
  notionalUsd: number
  /** `notionalUsd` as a share of the whole book (0–1). */
  share: number
  hasPerp: boolean
  hasOption: boolean
  positions: PositionRisk[]
  /** The perps held on this asset, for funding and live tickers. */
  perps: { symbol: string; exchange: Exchange }[]
  openOrderCount: number
}

export function buildExposure(
  risks: readonly PositionRisk[],
  openOrderSymbols: readonly string[] = []
): AssetExposure[] {
  const byAsset = new Map<string, AssetExposure>()
  const entry = (asset: string) => {
    let current = byAsset.get(asset)
    if (!current) {
      current = {
        asset,
        notionalUsd: 0,
        share: 0,
        hasPerp: false,
        hasOption: false,
        positions: [],
        perps: [],
        openOrderCount: 0,
      }
      byAsset.set(asset, current)
    }
    return current
  }

  for (const risk of risks) {
    const { position } = risk
    const exposure = entry(baseAssetOf(position.symbol))
    exposure.positions.push(risk)
    exposure.notionalUsd += positionNotionalUsd(position)
    if (isOptionSymbol(position.symbol)) exposure.hasOption = true
    if (isPerpSymbol(position.symbol)) {
      exposure.hasPerp = true
      if (!exposure.perps.some((perp) => perp.symbol === position.symbol)) {
        exposure.perps.push({ symbol: position.symbol, exchange: position.exchange })
      }
    }
  }
  for (const symbol of openOrderSymbols) entry(baseAssetOf(symbol)).openOrderCount += 1

  const total = [...byAsset.values()].reduce((sum, exposure) => sum + exposure.notionalUsd, 0)
  return [...byAsset.values()]
    .map((exposure) => ({ ...exposure, share: total > 0 ? exposure.notionalUsd / total : 0 }))
    .sort((a, b) => b.notionalUsd - a.notionalUsd || b.openOrderCount - a.openOrderCount)
}

/**
 * A perp's funding rate as an annual percentage. The rate is per funding period; the period
 * comes from the instrument ("480m" on Bybit) and defaults to 8 hours (Deribit sends none).
 */
export function fundingAprPercent(rate: number, interval?: string): number {
  const minutes = parseFloat(interval ?? '')
  const hours = Number.isFinite(minutes) && minutes > 0 ? minutes / 60 : 8
  return rate * (24 / hours) * 365 * 100
}
